/**
 * src/pages/CriticalThinking/data/arguments.data.ts
 * 6 Fictional everyday arguments for the Argument Reconstructor tool.
 * Covers: advertisement, workplace decision, public discussion, scientific claim,
 * neighbourhood dispute, and family decision.
 * Teaches: principle of charity, steelmanning, premise sorting, hidden assumptions,
 * scope/quantifiers, and distinguishing argument critique from personal attack.
 */

export interface ArgumentSentence {
  id: string;
  text: string;
  isPremise: boolean;
  isSideRemark: boolean;
  label: string;
}

export interface ReconstructedArgumentCase {
  id: string;
  domain: string;
  title: string;
  rawText: string;
  context: string;
  // Step 1: Identify conclusion
  conclusionOptions: {
    id: string;
    text: string;
    isCorrect: boolean;
    explanation: string;
  }[];
  // Step 2: Premise vs Side remarks sorting
  sentences: ArgumentSentence[];
  // Step 3: Identify hidden assumptions
  hiddenAssumptionOptions: {
    id: string;
    text: string;
    isCorrect: boolean;
    explanation: string;
  }[];
  // Step 4: Structural flaw / vulnerability
  vulnerabilityOptions: {
    id: string;
    text: string;
    isCorrect: boolean;
    explanation: string;
  }[];
  // Step 5: Reconstruction standard form
  standardForm: {
    premises: string[];
    hiddenPremises: string[];
    conclusion: string;
  };
  // Step 6: Steelmanned / Strongest-reasonable version
  steelmanVersion: {
    premises: string[];
    conclusion: string;
    explanation: string;
  };
  // Improvement guidance
  improvementWays: string[];
  // Explicit distinction between criticizing reasoning vs attacking the person
  charityAndPersonNote: string;
}

export const ARGUMENT_CASES: ReconstructedArgumentCase[] = [
  {
    id: "workplace-cloud",
    domain: "Workplace Decision",
    title: "The Rapid Cloud Migration Proposal",
    rawText: "Look, every modern tech company in our sector is already hosting their entire database on CloudSync. Our current on-premise servers are three years old anyway, and honestly, the IT department is always whining about maintenance headaches. If we don't migrate our financial ledger by the end of next month, our competitors will completely crush us in agility. Therefore, we need to approve the immediate migration contract this afternoon.",
    context: "A project manager pitching senior directors at a Monday morning executive operations meeting.",
    conclusionOptions: [
      {
        id: "c1",
        text: "The IT department is always complaining about maintenance.",
        isCorrect: false,
        explanation: "This is a descriptive side observation/grievance, not the primary action advocated."
      },
      {
        id: "c2",
        text: "We must immediately sign and approve the migration contract to CloudSync this afternoon.",
        isCorrect: true,
        explanation: "This is the core action and prescriptive conclusion the entire statement aims to justify."
      },
      {
        id: "c3",
        text: "Our current servers are three years old.",
        isCorrect: false,
        explanation: "This is an evidentiary premise offered to support the conclusion, not the conclusion itself."
      }
    ],
    sentences: [
      {
        id: "s1",
        text: "Every modern tech company in our sector is already hosting their entire database on CloudSync.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 1 (Appeal to industry popularity / peer behavior)"
      },
      {
        id: "s2",
        text: "Our current on-premise servers are three years old anyway.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 2 (Hardware age assertion)"
      },
      {
        id: "s3",
        text: "Honestly, the IT department is always whining about maintenance headaches.",
        isPremise: false,
        isSideRemark: true,
        label: "Side remark (Subjective emotional gripe / ad-hominem framing)"
      },
      {
        id: "s4",
        text: "If we don't migrate our financial ledger by the end of next month, our competitors will completely crush us in agility.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 3 (Catastrophic conditional threat / false dilemma)"
      }
    ],
    hiddenAssumptionOptions: [
      {
        id: "h1",
        text: "CloudSync has zero security, regulatory, or migration downtime risks that could outweigh the agility gains.",
        isCorrect: true,
        explanation: "Essential for the leap from 'others use it' to 'we must sign today without standard vendor auditing'."
      },
      {
        id: "h2",
        text: "Signing today is the only way to avoid immediate corporate insolvency next month.",
        isCorrect: true,
        explanation: "The argument assumes a false dilemma where a measured, staged review is impossible."
      },
      {
        id: "h3",
        text: "The IT staff all have college degrees in computer science.",
        isCorrect: false,
        explanation: "Irrelevant background assumption that has no logical bearing on the migration decision."
      }
    ],
    vulnerabilityOptions: [
      {
        id: "v1",
        text: "False dilemma & appeal to panic: assumes failing to migrate within 30 days leads to total destruction, while ignoring compliance, data residency, and migration costs.",
        isCorrect: true,
        explanation: "The core weakness is unquantified panic and bandwagon pressure masquerading as rational risk analysis."
      },
      {
        id: "v2",
        text: "The speaker is using words with more than three syllables.",
        isCorrect: false,
        explanation: "Vocabulary length is not a logical defect."
      },
      {
        id: "v3",
        text: "Cloud servers do not actually exist in the physical world.",
        isCorrect: false,
        explanation: "Factually confused: cloud servers are physical machines hosted off-site."
      }
    ],
    standardForm: {
      premises: [
        "Premise 1: Competitors in our sector are increasingly utilizing cloud database providers.",
        "Premise 2: Our on-premise infrastructure is three years old and incurs ongoing maintenance costs.",
        "Premise 3: Greater operational agility is valuable for our market competitiveness."
      ],
      hiddenPremises: [
        "Hidden Assumption A: The benefits of CloudSync exceed its security, contractual, and migration costs.",
        "Hidden Assumption B: An emergency decision today is superior to a 60-day structured technical audit."
      ],
      conclusion: "Conclusion: We should immediately authorize the CloudSync contract today."
    },
    steelmanVersion: {
      premises: [
        "P1: Our on-premise hardware is approaching end-of-lifecycle and requires escalating IT maintenance hours.",
        "P2: Modern cloud infrastructure offers automated scaling and failover that would improve engineering deployment speed.",
        "P3: Competitors with cloud platforms can deploy updates faster than our current infrastructure allows."
      ],
      conclusion: "Steelmanned Conclusion: We should commission an accelerated 30-day technical and compliance feasibility review of CloudSync to modernize our infrastructure.",
      explanation: "A steelmanned reconstruction removes the frantic hyperbole ('crush us next month') and personal slights ('IT is whining'), recasting the argument as a legitimate operational modernization proposal with a sensible risk-audit timeline."
    },
    improvementWays: [
      "Provide quantified cost-benefit estimates comparing cloud subscription fees vs server refresh capital expenditure.",
      "Include security compliance verification (e.g. SOC2, GDPR data residency for financial ledgers).",
      "Replace the false ultimatum ('sign today or die') with a phased migration timeline that respects IT testing."
    ],
    charityAndPersonNote: "Critique the argument, not the advocate: It is tempting to dismiss the speaker as 'impatient, manipulative, or buzzword-obsessed'. But critical thinking focuses on the evidentiary gaps (lack of security audit, false urgency) rather than attacking the speaker's personality."
  },

  {
    id: "ad-skincare",
    domain: "Advertisement",
    title: "The Cellular Oxygen Skin Elixir",
    rawText: "Nature never makes mistakes, and for thousands of years our ancestors lived without synthetic chemical cosmetics. Our PureVibe Serum contains only 100% cold-pressed botanical oils infused with pure oxygen. If you care about your body's cellular longevity and refuse to poison your pores with artificial lab petroleum, PureVibe is the only moral, clean choice for your morning routine.",
    context: "A sponsored social media video advertisement for a boutique direct-to-consumer skincare brand.",
    conclusionOptions: [
      {
        id: "c1",
        text: "Synthetic cosmetics contain petroleum derivatives.",
        isCorrect: false,
        explanation: "This is a stated factual premise used in the sales pitch, not the ultimate conclusion."
      },
      {
        id: "c2",
        text: "You should purchase and use PureVibe Serum as your morning skincare product.",
        isCorrect: true,
        explanation: "The prescriptive marketing conclusion urging consumer purchase and behavioral adoption."
      },
      {
        id: "c3",
        text: "Our ancestors never used smartphones.",
        isCorrect: false,
        explanation: "Not mentioned or claimed."
      }
    ],
    sentences: [
      {
        id: "s1",
        text: "Nature never makes mistakes, and for thousands of years our ancestors lived without synthetic chemical cosmetics.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 1 (Appeal to nature & appeal to antiquity)"
      },
      {
        id: "s2",
        text: "Our PureVibe Serum contains only 100% cold-pressed botanical oils infused with pure oxygen.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 2 (Product composition claim)"
      },
      {
        id: "s3",
        text: "If you care about your body's cellular longevity and refuse to poison your pores with artificial lab petroleum, PureVibe is the only moral, clean choice.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 3 (False dilemma & loaded emotional framing)"
      }
    ],
    hiddenAssumptionOptions: [
      {
        id: "h1",
        text: "Everything natural is inherently safe and beneficial, while everything synthetic is inherently toxic and harmful (the appeal to nature fallacy).",
        isCorrect: true,
        explanation: "Central unstated axiom: poison ivy and botulinum are 100% natural, while lab-purified medicines save lives."
      },
      {
        id: "h2",
        text: "Skin pores can metabolize dissolved gaseous oxygen directly from topically applied vegetable oil.",
        isCorrect: true,
        explanation: "Unsubstantiated biophysical assumption bridging the product description to 'cellular longevity'."
      },
      {
        id: "h3",
        text: "The bottle is made of recycled amber glass.",
        isCorrect: false,
        explanation: "Packaging detail not logically required for the efficacy claim."
      }
    ],
    vulnerabilityOptions: [
      {
        id: "v1",
        text: "Naturalistic fallacy & false dilemma: equivocates 'natural' with 'healthy', uses guilt-laden moralistic language ('only moral choice'), and presents a false binary between their serum and toxic poisoning.",
        isCorrect: true,
        explanation: "Spotlights the core manipulation: exploiting emotional purity instincts rather than clinical dermatology."
      },
      {
        id: "v2",
        text: "Botanical oils do not exist in plant seeds.",
        isCorrect: false,
        explanation: "Factually false: plant seeds contain real oils."
      },
      {
        id: "v3",
        text: "Oxygen is dangerous to human lungs.",
        isCorrect: false,
        explanation: "Oxygen is vital for aerobic respiration, though topical oil absorption is a different matter."
      }
    ],
    standardForm: {
      premises: [
        "Premise 1: Natural botanical substances are inherently superior and safer than laboratory-synthesized compounds.",
        "Premise 2: PureVibe Serum is derived from botanical plant oils.",
        "Premise 3: Synthetic ingredients in conventional cosmetics are toxic hazards."
      ],
      hiddenPremises: [
        "Hidden Assumption: Topical botanical oil enhances cellular longevity without risk of irritation or comedogenic blocking."
      ],
      conclusion: "Conclusion: Consumers who value their health should exclusively use PureVibe Serum."
    },
    steelmanVersion: {
      premises: [
        "P1: Some consumers experience sensitivity or allergic dermatitis to specific synthetic preservatives and fragrance compounds.",
        "P2: PureVibe Serum contains plant-based lipid emollients that provide basic stratum corneum moisture retention without synthetic perfumes."
      ],
      conclusion: "Steelmanned Conclusion: For consumers seeking fragrance-free, plant-oil based emollient moisturizers, PureVibe offers a gentle, minimalist formulation option.",
      explanation: "Stripping away the moralizing purity guilt ('poison your pores') and bogus cellular anti-aging claims reveals a modest, sensible product: an unperfumed plant lipid moisturizer for sensitive skin."
    },
    improvementWays: [
      "Cite independent clinical patch testing demonstrating low comedogenicity and hypoallergenic safety.",
      "Drop moralistic shame framing ('the only moral choice') in favor of verifiable dermatological metrics.",
      "Acknowledge that synthesized ingredients (like purified hyaluronic acid or USP-grade petrolatum) have extensive safety profiles."
    ],
    charityAndPersonNote: "Target the rhetoric, not the seller's moral soul: Calling marketing copywriters 'evil scam artists' does not analyze the argument. Identifying the naturalistic fallacy and false binary deconstructs the persuasion mechanism with surgical objectivity."
  },

  {
    id: "public-transit",
    domain: "Public Discussion",
    title: "The Downtown Bike Lane Debate",
    rawText: "I've owned my hardware store on Main Street for twenty-five years, and anyone who thinks painting green bike lanes through the commercial district makes sense has clearly never run a business. A bike lane will eliminate 34 curbside parking spots! If shoppers can't park their cars directly in front of the shop, they won't shop here at all, our revenue will collapse, and Main Street will turn into a ghost town. The city planners pushing this are just out-of-touch bureaucrats who hate drivers.",
    context: "A small business owner addressing a city zoning commission public hearing.",
    conclusionOptions: [
      {
        id: "c1",
        text: "The city planners hate drivers.",
        isCorrect: false,
        explanation: "This is an ad-hominem motive speculation, not the policy proposal under debate."
      },
      {
        id: "c2",
        text: "The city council should reject the proposed Main Street bike lane plan.",
        isCorrect: true,
        explanation: "The core prescriptive policy conclusion the speaker is lobbying the zoning board to adopt."
      },
      {
        id: "c3",
        text: "The speaker has owned a hardware store for twenty-five years.",
        isCorrect: false,
        explanation: "An autobiographical credential premise establishing personal standing, not the conclusion."
      }
    ],
    sentences: [
      {
        id: "s1",
        text: "I've owned my hardware store on Main Street for twenty-five years, and anyone who thinks painting green bike lanes makes sense has never run a business.",
        isPremise: false,
        isSideRemark: true,
        label: "Side remark / Ad-hominem credentialing (Poisoning the well against proponents)"
      },
      {
        id: "s2",
        text: "A bike lane will eliminate 34 curbside parking spots.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 1 (Physical parking capacity reduction assertion)"
      },
      {
        id: "s3",
        text: "If shoppers can't park directly in front of the shop, they won't shop here at all, our revenue will collapse, and Main Street will turn into a ghost town.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 2 (Slippery slope causal projection)"
      },
      {
        id: "s4",
        text: "The city planners pushing this are just out-of-touch bureaucrats who hate drivers.",
        isPremise: false,
        isSideRemark: true,
        label: "Side remark (Attacking opponent motives rather than evaluating traffic flow)"
      }
    ],
    hiddenAssumptionOptions: [
      {
        id: "h1",
        text: "The vast majority of Main Street retail revenue comes exclusively from customers who park in those specific 34 curbside spaces, rather than pedestrians, cyclists, transit riders, or people parking in nearby municipal garages.",
        isCorrect: true,
        explanation: "Vital unexamined assumption: merchant surveys often dramatically overestimate the fraction of customers arriving by curbside car."
      },
      {
        id: "h2",
        text: "Bicycles travel faster than supersonic jets.",
        isCorrect: false,
        explanation: "Absurd irrelevant proposition."
      },
      {
        id: "h3",
        text: "Hardware items can only be transported in pickup trucks.",
        isCorrect: false,
        explanation: "While hardware can be heavy, this is a secondary assumption compared to the universal retail claim."
      }
    ],
    vulnerabilityOptions: [
      {
        id: "v1",
        text: "Slippery slope & unevidenced causal leap: assumes removing 34 spaces automatically causes total retail extinction, ignores off-street parking supply, and dismisses foot/bike customer spending with ad-hominem slurs.",
        isCorrect: true,
        explanation: "Identifies both the logical trajectory (slippery slope) and evidentiary blindspot (ignoring multi-modal retail studies)."
      },
      {
        id: "v2",
        text: "Hardware stores should sell bicycles instead of hammers.",
        isCorrect: false,
        explanation: "Not a logical critique of the argument."
      },
      {
        id: "v3",
        text: "The speaker is older than fifty years old.",
        isCorrect: false,
        explanation: "Age has no bearing on argument validity."
      }
    ],
    standardForm: {
      premises: [
        "Premise 1: The proposed bike lane project removes 34 curbside vehicle parking spaces on Main Street.",
        "Premise 2: Curbside parking convenience is a major factor in customer store patronage.",
        "Premise 3: A substantial reduction in customer visits will threaten the financial solvency of local merchants."
      ],
      hiddenPremises: [
        "Hidden Assumption: Shoppers will not use adjacent parking structures or arrive via foot, bus, or bicycle."
      ],
      conclusion: "Conclusion: The city council should reject the bike lane project."
    },
    steelmanVersion: {
      premises: [
        "P1: Merchants on Main Street, particularly those selling heavy goods (like hardware and appliances), rely on nearby vehicle loading zones for customer pickups.",
        "P2: Removing 34 curbside parking spaces without designated loading bays or signage pointing to adjacent municipal lots risks short-term customer disruption during peak shopping hours.",
        "P3: Sustaining independent small businesses is an important economic and community goal for downtown vitality."
      ],
      conclusion: "Steelmanned Conclusion: Before finalizing the bike lane design, the city should incorporate dedicated freight/loading zones and evaluate off-street parking capacity to protect retail accessibility.",
      explanation: "By replacing the emotional catastrophizing ('ghost town', 'bureaucrats hate drivers') with legitimate merchant logistics (heavy goods loading, transitional disruption), the concern becomes a solvable urban design problem."
    },
    improvementWays: [
      "Gather real merchant customer data: survey how shoppers currently arrive (car, foot, bike, transit).",
      "Propose constructive mitigation: request replacement short-term loading bays at street corners instead of outright project cancellation.",
      "Examine empirical case studies from peer cities measuring sales tax revenue before and after bike lane installations."
    ],
    charityAndPersonNote: "Principle of charity in heated civic debates: It is easy to brand the shopkeeper as an 'anti-cycling dinosaur'. Applying charity recognizes their legitimate, terrifying fear of losing their life's livelihood, separating that valid anxiety from their flawed slippery-slope argument."
  },

  {
    id: "science-diet",
    domain: "Scientific Claim",
    title: "The Superfood Longevity Extrapolation",
    rawText: "Researchers at State University just proved that compound B-14, found in wild arctic berries, completely halted telomere shortening in lab mice bred with accelerated aging genes. The treated mice lived 28% longer than control mice! This proves that drinking two glasses of arctic berry juice daily will extend human lifespan by twenty years. Anyone who questions this simply hates scientific progress.",
    context: "A popular science journalism headline and introductory article in a digital lifestyle magazine.",
    conclusionOptions: [
      {
        id: "c1",
        text: "Drinking arctic berry juice daily will extend human lifespan by roughly twenty years.",
        isCorrect: true,
        explanation: "The ultimate real-world health and longevity conclusion asserted in the article."
      },
      {
        id: "c2",
        text: "Compound B-14 halted telomere shortening in accelerated-aging mice.",
        isCorrect: false,
        explanation: "The actual empirical finding of the study, used as a premise to extrapolate the human claim."
      },
      {
        id: "c3",
        text: "State University has an operating biology laboratory.",
        isCorrect: false,
        explanation: "An incidental background fact."
      }
    ],
    sentences: [
      {
        id: "s1",
        text: "Researchers at State University showed compound B-14 halted telomere shortening in lab mice bred with accelerated aging genes.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 1 (Empirical rodent finding)"
      },
      {
        id: "s2",
        text: "The treated mice lived 28% longer than control mice in the trial.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 2 (Quantitative animal survival outcome)"
      },
      {
        id: "s3",
        text: "Compound B-14 is found in wild arctic berries.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 3 (Botanical occurrence premise)"
      },
      {
        id: "s4",
        text: "Anyone who questions this simply hates scientific progress.",
        isPremise: false,
        isSideRemark: true,
        label: "Side remark (Poisoning the well / ad-hominem dismissal of skeptics)"
      }
    ],
    hiddenAssumptionOptions: [
      {
        id: "h1",
        text: "Human metabolic pharmacokinetics are identical to genetically modified mice, the concentration in dietary juice matches high-potency lab extracts, and compound B-14 survives human digestion intact.",
        isCorrect: true,
        explanation: "The enormous translational bridge required to jump from in-vivo rodent assays to human dietary longevity."
      },
      {
        id: "h2",
        text: "Mice and humans have the exact same fur thickness.",
        isCorrect: false,
        explanation: "Irrelevant anatomical trivia."
      },
      {
        id: "h3",
        text: "Berries grow on pine trees.",
        isCorrect: false,
        explanation: "Factually incorrect botanical statement with no logical bearing."
      }
    ],
    vulnerabilityOptions: [
      {
        id: "v1",
        text: "Unwarranted animal-to-human extrapolation & dosing equivocation: assumes lab-purified compounds behave identically in human digestion, leaps from 28% rodent lifespan to a 20-year human guarantee, and silences critics.",
        isCorrect: true,
        explanation: "Accurately diagnoses the number-one error in sensationalized biomedical science journalism."
      },
      {
        id: "v2",
        text: "State University does not have a football stadium.",
        isCorrect: false,
        explanation: "Completely irrelevant to biology research."
      },
      {
        id: "v3",
        text: "Telomeres do not exist inside eukaryotic cells.",
        isCorrect: false,
        explanation: "Factually false: telomeres are well-established chromosome end-caps."
      }
    ],
    standardForm: {
      premises: [
        "Premise 1: Compound B-14 extended the lifespan of accelerated-aging mice in controlled lab conditions by 28%.",
        "Premise 2: Compound B-14 occurs naturally in wild arctic berries.",
        "Premise 3: Human telomere biology shares structural similarities with rodent cellular mechanisms."
      ],
      hiddenPremises: [
        "Hidden Assumption: Consuming dietary berry juice delivers biologically active concentrations of B-14 across the human intestinal barrier without toxicity."
      ],
      conclusion: "Conclusion: Drinking arctic berry juice will extend human life by twenty years."
    },
    steelmanVersion: {
      premises: [
        "P1: Compound B-14 demonstrated measurable telomere preservation and lifespan extension in a preliminary rodent model.",
        "P2: Natural botanical polyphenols frequently serve as leads for pharmaceutical anti-aging drug discovery.",
        "P3: Understanding the biochemical pathway of B-14 may reveal therapeutic targets for human cellular senescence."
      ],
      conclusion: "Steelmanned Conclusion: Compound B-14 is a promising candidate for further mechanistic and translational research into cellular aging pathways.",
      explanation: "A steelmanned scientific read honors the exciting basic biology finding without making reckless, unevidenced commercial health promises to consumers."
    },
    improvementWays: [
      "Distinguish clearly between basic in-vitro/rodent models and Phase I human clinical trials.",
      "Calculate realistic dietary equivalents: explain that getting the mouse dose might require drinking 400 liters of juice daily.",
      "Frame the result as an interesting biological mechanism rather than an established consumer longevity cure."
    ],
    charityAndPersonNote: "Science communication integrity: Do not dismiss the science writer as 'stupid or corrupt'. Rather, identify the structural vulnerability in their inference chain: jumping from rodent telomere biology to human grocery store guidance."
  },

  {
    id: "neighbour-fence",
    domain: "Neighbourhood Dispute",
    title: "The Boundary Tree Controversy",
    rawText: "The maple tree branches on Mark's side of the property line hang over my roof. Every autumn, wet leaves clog my gutters, and during last month's windstorm, a small twig tapped my bedroom window. If that entire massive tree ever blows over in a hurricane, it will crush my master bedroom and kill my family in their sleep! Trees in urban yards are hazards. Therefore, the municipal bylaws must compel Mark to completely chop down the entire seventy-year-old tree immediately.",
    context: "A homeowner submitting a formal complaint and petition to a suburban municipal code compliance board.",
    conclusionOptions: [
      {
        id: "c1",
        text: "Wet leaves clog gutters in autumn.",
        isCorrect: false,
        explanation: "An ordinary maintenance premise, not the administrative penalty requested."
      },
      {
        id: "c2",
        text: "The city must legally force the neighbor to cut down the entire mature maple tree.",
        isCorrect: true,
        explanation: "The specific punitive and legal remedy the homeowner is demanding."
      },
      {
        id: "c3",
        text: "Mark bought a new lawnmower last spring.",
        isCorrect: false,
        explanation: "Unrelated."
      }
    ],
    sentences: [
      {
        id: "s1",
        text: "The maple tree branches on Mark's side of the property line hang over my roof.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 1 (Physical spatial overhang assertion)"
      },
      {
        id: "s2",
        text: "Every autumn, wet leaves clog my gutters, and during last month's windstorm, a small twig tapped my bedroom window.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 2 (Minor nuisance / observation of contact)"
      },
      {
        id: "s3",
        text: "If that entire massive tree ever blows over in a hurricane, it will crush my master bedroom and kill my family in their sleep!",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 3 (Catastrophic worst-case projection)"
      },
      {
        id: "s4",
        text: "Trees in urban yards are hazards.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 4 (Broad categorical overgeneralization)"
      }
    ],
    hiddenAssumptionOptions: [
      {
        id: "h1",
        text: "A healthy 70-year-old tree has a high likelihood of sudden total structural uprooting, and standard trimming of overhanging limbs is completely inadequate to mitigate roof risks.",
        isCorrect: true,
        explanation: "Assumes total removal is the only viable remedy, ignoring routine professional pruning."
      },
      {
        id: "h2",
        text: "Maple trees produce maple syrup only in winter.",
        isCorrect: false,
        explanation: "Irrelevant agricultural trivia."
      },
      {
        id: "h3",
        text: "Gutters can never be cleaned by hand.",
        isCorrect: false,
        explanation: "Hyperbolic, but not the core structural assumption governing the tree removal demand."
      }
    ],
    vulnerabilityOptions: [
      {
        id: "v1",
        text: "Disproportionate remedy & catastrophic fallacy: leaps from minor manageable nuisances (leaves, twig tap) to a lethal catastrophic scenario without arborist risk inspection, demanding total felling instead of trimming.",
        isCorrect: true,
        explanation: "Pinpoints the mismatch between the minor evidence (leaves) and the extreme remedy (destroying a healthy 70-year-old tree)."
      },
      {
        id: "v2",
        text: "Windows should always be constructed from titanium.",
        isCorrect: false,
        explanation: "Architectural absurdity."
      },
      {
        id: "v3",
        text: "Hurricanes only happen in tropical oceans.",
        isCorrect: false,
        explanation: "Severe windstorms occur in many temperate regions."
      }
    ],
    standardForm: {
      premises: [
        "Premise 1: Tree branches overhang the complainant's roof, depositing leaves and making occasional contact in storms.",
        "Premise 2: Falling mature trees have the physical mass to cause structural residential damage.",
        "Premise 3: Property owners have an obligation to prevent foreseeable hazards to adjoining neighbors."
      ],
      hiddenPremises: [
        "Hidden Assumption: The tree is diseased or unstable, and pruning the overhanging branches is insufficient to ensure safety."
      ],
      conclusion: "Conclusion: The neighbor must be legally forced to remove the entire tree."
    },
    steelmanVersion: {
      premises: [
        "P1: Overhanging mature branches can cause gutter blockages, roof shingle wear, and potential damage during severe gale-force wind events.",
        "P2: Property owners should take reasonable steps to prevent their foliage from encroaching destructively on neighboring structures.",
        "P3: Certified arborists can assess branch integrity and safely remove risk-bearing lateral limbs without killing the tree."
      ],
      conclusion: "Steelmanned Conclusion: Mark should coordinate with an arborist to trim back the specific encroaching branches that pose a hazard to the neighbor's roof.",
      explanation: "A steelmanned compromise addresses the legitimate roof-clearance safety concern without adopting the disproportionate, destructive demand to fell the entire neighborhood tree."
    },
    improvementWays: [
      "Commission a qualified arborist report assessing structural health, root stability, and branch load.",
      "Narrow the requested remedy from 'total felling' to 'pruning overhanging limbs back to the boundary line' (which many property codes permit).",
      "Remove emotionally manipulative hyperbole ('kill my family') in favor of objective clearance measurements."
    ],
    charityAndPersonNote: "De-escalating domestic and neighbor conflicts: The complainant is feeling genuine catastrophic anxiety about family safety. Validating that safety fear while showing that professional pruning solves the issue allows dispute resolution without neighborly war."
  },

  {
    id: "family-college",
    domain: "Family Decision",
    title: "The Degree vs Apprenticeship Dilemma",
    rawText: "Uncle Dave dropped out of college at nineteen and now he owns a luxury boat and three rental properties. Meanwhile, your cousin Elena spent four years getting an art history degree and now works as an unpaid intern at a gallery. This clearly proves that going to university is an absolute waste of time and money in today's economy. You should turn down your university scholarship and immediately become an electrician's apprentice.",
    context: "A parent advising their high school senior over dinner regarding post-graduation career choices.",
    conclusionOptions: [
      {
        id: "c1",
        text: "Uncle Dave owns a luxury boat.",
        isCorrect: false,
        explanation: "An isolated biographical anecdote used as persuasive illustration."
      },
      {
        id: "c2",
        text: "You should decline your university scholarship and pursue an electrician apprenticeship instead.",
        isCorrect: true,
        explanation: "The core prescriptive advice being urged upon the student."
      },
      {
        id: "c3",
        text: "Art history degrees are taught in universities.",
        isCorrect: false,
        explanation: "A general educational fact."
      }
    ],
    sentences: [
      {
        id: "s1",
        text: "Uncle Dave dropped out of college at nineteen and now owns a luxury boat and three rental properties.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 1 (Anecdotal success case)"
      },
      {
        id: "s2",
        text: "Cousin Elena spent four years getting an art history degree and works as an unpaid intern.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 2 (Anecdotal struggle case)"
      },
      {
        id: "s3",
        text: "This clearly proves that going to university is an absolute waste of time and money in today's economy.",
        isPremise: true,
        isSideRemark: false,
        label: "Premise 3 (Sweeping categorical generalization)"
      }
    ],
    hiddenAssumptionOptions: [
      {
        id: "h1",
        text: "Two isolated family anecdotes represent the entire macro-economic distribution of college graduate earnings versus skilled trade apprenticeships, ignoring broad statistical averages and the student's individual scholarship offer.",
        isCorrect: true,
        explanation: "The fundamental flaw: substituting two vivid family stories for large-sample demographic evidence."
      },
      {
        id: "h2",
        text: "Electricians never have to work on weekends.",
        isCorrect: false,
        explanation: "Irrelevant scheduling assumption."
      },
      {
        id: "h3",
        text: "All boats are expensive to maintain.",
        isCorrect: false,
        explanation: "Trivial maritime trivia."
      }
    ],
    vulnerabilityOptions: [
      {
        id: "v1",
        text: "Anecdotal fallacy & hasty generalization: bases a massive life decision on two hand-picked relatives (n=2), ignores the student's specific scholarship (zero tuition debt), and conflates all degree disciplines into a single caricature.",
        isCorrect: true,
        explanation: "Shows how emotional proximity blinds people to base rates and individual circumstances."
      },
      {
        id: "v2",
        text: "Electricity was discovered by Benjamin Franklin using a kite.",
        isCorrect: false,
        explanation: "Historical anecdote with zero logical bearing on education choices."
      },
      {
        id: "v3",
        text: "Art galleries are illegal in small towns.",
        isCorrect: false,
        explanation: "Nonsensical."
      }
    ],
    standardForm: {
      premises: [
        "Premise 1: Skilled trade careers can lead to high earnings and financial independence without student debt.",
        "Premise 2: Some university degrees carry high opportunity costs and weak direct vocational pipelines.",
        "Premise 3: Excessive student loan debt impairs early adult financial stability."
      ],
      hiddenPremises: [
        "Hidden Assumption: The specific university program and student's funded scholarship offer will yield lower lifetime fulfillment and financial return than an electrical apprenticeship."
      ],
      conclusion: "Conclusion: You should decline the university scholarship in favor of an apprenticeship."
    },
    steelmanVersion: {
      premises: [
        "P1: Skilled electrical trades currently offer strong job security, paid training, and freedom from high tuition debt in an uncertain job market.",
        "P2: High college tuition debt can strain young adults if chosen degrees do not lead to marketable vocational careers.",
        "P3: Any major educational choice should be weighed against debt burden, vocational demand, and personal aptitude."
      ],
      conclusion: "Steelmanned Conclusion: Before committing to university, carefully evaluate the degree's career prospects and compare the debt-free earning potential of skilled trades against your scholarship package.",
      explanation: "A steelmanned version honors the parent's genuine, loving concern about student debt and economic precarity, without forcing a rigid false binary based on two cherry-picked relatives."
    },
    improvementWays: [
      "Consult objective census bureau lifetime earnings data across specific majors and trade certifications.",
      "Factor in the scholarship: a funded scholarship changes the debt calculation entirely.",
      "Assess the student's personal aptitudes, interests, and long-term vocational goals rather than imposing Uncle Dave's path."
    ],
    charityAndPersonNote: "Charity in family relationships: Parents often use dramatic anecdotes because they are terrified of their children struggling financially. Understanding the protective motivation behind the bad argument enables a calm, data-informed conversation rather than an explosive family feud."
  }
];
